import { useAuth } from '@/contexts/AuthContext';
import { Truck, Package, Users, Euro, X, MapPin, Calendar, Weight } from 'lucide-react';
import { DashboardHeader } from '@/components/dashboard/DashboardHeader';
import { StatsGrid } from '@/components/dashboard/StatsGrid';
import { DashboardStat } from '@/types/dashboard';
import { useTransportJobs } from '@/hooks/useTransportJobs';
import { useVehicles } from '@/hooks/useVehicles';
import { useDrivers } from '@/hooks/useDrivers';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { format } from 'date-fns';
import { fr, enUS, es, de, it, type Locale } from 'date-fns/locale';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

const localeMap: Record<string, Locale> = {
  fr,
  en: enUS,
  es,
  de,
  it,
};

const TransporterDashboard = () => {
  const { user } = useAuth();
  const { t, i18n } = useTranslation();
  const { jobs, deleteJob, isDeleting } = useTransportJobs();
  const { vehicles, availableVehicles } = useVehicles();
  const { drivers, availableDrivers } = useDrivers();

  const activeJobs = jobs.filter(j => !j.completed_at && !j.declined_at);
  const completedJobs = jobs.filter(j => j.completed_at);

  const handleCancelJob = async (jobId: string, orderNumber: string) => {
    if (!confirm(t('transporter.dashboard.cancelMissionConfirm', { orderNumber }))) {
      return;
    }
    
    try {
      await deleteJob(jobId);
      toast.success(t('transporter.messages.missionCanceled'));
    } catch (error: any) {
      toast.error(error.message || t('transporter.messages.cancelError'));
    }
  };

  const stats: DashboardStat[] = [
    {
      title: t('transporter.stats.availableVehicles'),
      value: availableVehicles.length,
      icon: Truck,
      color: 'text-[#0880AE]',
      bgColor: 'bg-[#0880AE]/10',
    },
    {
      title: t('transporter.stats.activeMissions'),
      value: activeJobs.length,
      icon: Package,
      color: 'text-[#FFBF07]',
      bgColor: 'bg-[#FFBF07]/10',
    },
    {
      title: t('transporter.stats.availableDrivers'),
      value: availableDrivers.length,
      icon: Users,
      color: 'text-[#37699E]',
      bgColor: 'bg-[#37699E]/10',
    },
    {
      title: t('transporter.stats.completedMissions'),
      value: completedJobs.length,
      icon: Euro,
      color: 'text-[#14A38B]',
      bgColor: 'bg-[#14A38B]/10',
    },
  ];

  return (
    <div className="space-y-8">
      <div className="max-w-7xl mx-auto">
        <DashboardHeader 
          title={t('transporter.dashboard.title')}
          userName={user?.name || user?.email}
        />

        <div className="mb-8">
          <StatsGrid stats={stats} />
        </div>

        <Card className="rounded-xl shadow-md">
          <CardHeader className="pb-3">
            <CardTitle className="text-xl font-medium text-[#37699E] font-heading">
              {t('transporter.dashboard.activeMissions')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {activeJobs.length === 0 ? (
              <div className="text-center py-12">
                <Package className="mx-auto h-12 w-12 text-[#87ADD4] mb-4" />
                <p className="text-[#5A6B7A]">{t('transporter.dashboard.noActiveMissions')}</p>
                <p className="text-sm text-[#87ADD4] mt-2">
                  {t('transporter.dashboard.checkMarketplace')}
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {activeJobs.map((job: any) => {
                  const isPending = !job.accepted_at && !job.declined_at;
                  
                  return (
                    <Card 
                      key={job.id} 
                      className={`rounded-lg ${
                        isPending 
                          ? 'border-2 border-[#F2AC57] bg-[#F2AC57]/5' 
                          : 'border border-[#DBE2EA]'
                      }`}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex-1">
                            <h4 className="font-medium text-[#2D3748] mb-1">
                              {job.orders?.order_number}
                            </h4>
                            <p className="text-sm text-[#5A6B7A]">
                              {job.orders?.cargo_title}
                            </p>
                          </div>
                          <div className="flex items-center gap-2">
                            <Badge className={
                              isPending
                                ? 'bg-[#F2AC57] text-white border-0'
                                : 'bg-[#FFBF07]/10 text-[#CC9805] border-0'
                            }>
                              {isPending 
                                ? t('transporter.mission.pendingConfirmation')
                                : job.assigned_at 
                                  ? t('transporter.mission.assigned')
                                  : t('transporter.mission.accepted')
                              }
                            </Badge>
                            {!job.accepted_at && !job.started_at && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleCancelJob(job.id, job.orders?.order_number)}
                                disabled={isDeleting}
                                className="h-8 w-8 p-0 text-[#FF7171] hover:text-[#E65959] hover:bg-[#FF7171]/10"
                              >
                                <X size={16} />
                              </Button>
                            )}
                          </div>
                        </div>
                      
                      <div className="space-y-3 text-sm">
                        <div className="flex items-start gap-2">
                          <MapPin className="text-[#0880AE] mt-0.5 flex-shrink-0" size={16} />
                          <div className="flex-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="text-[#5A6B7A] text-xs uppercase font-medium">{t('transporter.mission.pickup')}</span>
                              <span className="text-[#2D3748] font-medium">
                                {job.orders?.pickup_city}, {job.orders?.pickup_country}
                              </span>
                            </div>
                            {job.orders?.pickup_address && (
                              <p className="text-[#5A6B7A] text-xs mt-0.5">{job.orders.pickup_address}</p>
                            )}
                          </div>
                          {job.orders?.pickup_date && (
                            <div className="flex items-center gap-1 text-[#5A6B7A]">
                              <Calendar size={14} />
                              <span className="text-xs">{format(new Date(job.orders.pickup_date), 'dd MMM', { locale: localeMap[i18n.language] || fr })}</span>
                            </div>
                          )}
                        </div>

                        <div className="flex items-start gap-2">
                          <MapPin className="text-[#14A38B] mt-0.5 flex-shrink-0" size={16} />
                          <div className="flex-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="text-[#5A6B7A] text-xs uppercase font-medium">{t('transporter.mission.delivery')}</span>
                              <span className="text-[#2D3748] font-medium">
                                {job.orders?.delivery_city}, {job.orders?.delivery_country}
                              </span>
                            </div>
                            {job.orders?.delivery_address && (
                              <p className="text-[#5A6B7A] text-xs mt-0.5">{job.orders.delivery_address}</p>
                            )}
                          </div>
                          {job.orders?.delivery_date && (
                            <div className="flex items-center gap-1 text-[#5A6B7A]">
                              <Calendar size={14} />
                              <span className="text-xs">{format(new Date(job.orders.delivery_date), 'dd MMM', { locale: localeMap[i18n.language] || fr })}</span>
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="mt-3 pt-3 border-t border-[#DBE2EA]">
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
                          {job.orders?.weight_kg && (
                            <div className="flex items-center gap-2">
                              <Weight className="text-[#5A6B7A]" size={16} />
                              <span className="text-[#2D3748]">{job.orders.weight_kg} kg</span>
                            </div>
                          )}
                          {job.orders?.trailer_type && (
                            <div className="flex items-center gap-2">
                              <Package className="text-[#5A6B7A]" size={16} />
                              <span className="text-[#2D3748]">{job.orders.trailer_type}</span>
                            </div>
                          )}
                          {(job.orders?.final_price || job.orders?.proposed_price) && (
                            <div className="flex items-center gap-2">
                              <Euro className="text-[#14A38B]" size={16} />
                              <span className="text-[#2D3748] font-semibold">
                                {job.orders.final_price || job.orders.proposed_price} €
                              </span>
                            </div>
                          )}
                        </div>
                      </div>

                      {job.driver_id && (
                        <div className="mt-3 pt-3 border-t border-[#DBE2EA] text-sm">
                          <span className="text-[#5A6B7A]">{t('transporter.mission.driver')} </span>
                          <span className="text-[#2D3748]">
                            {job.drivers?.users?.first_name} {job.drivers?.users?.last_name}
                          </span>
                          {job.vehicles && (
                            <span className="text-[#5A6B7A] ml-4">
                              {t('transporter.mission.vehicle')} <span className="text-[#2D3748]">{job.vehicles?.plate_number}</span>
                            </span>
                          )}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default TransporterDashboard;
